import {useAuth} from '@common/auth/use-auth';
import {notificationBadgeCount} from '@common/notifications/notification-badge-count';
import {
  deleteNotificationsOptions,
  listNotificationsOptions,
  markNotificationsAsReadOptions,
} from '@common/notifications/notifications-queries';
import {
  isNotificationSoundEnabled,
  setNotificationSoundEnabled,
  unlockNotificationAudio,
} from '@common/notifications/play-notification-sound';
import {Button, LinkButton} from '@shadcn/button/button';
import {Popover} from '@shadcn/popover/popover';
import {Spinner} from '@shadcn/spinner/spinner';
import {useMutation, useQuery} from '@tanstack/react-query';
import {Trans} from '@ui/i18n/trans';
import {useSettings} from '@ui/settings/use-settings';
import {cn} from '@ui/utils/cn';
import {
  BellIcon,
  CheckCheckIcon,
  SettingsIcon,
  Trash2Icon,
  Volume2Icon,
  VolumeXIcon,
} from 'lucide-react';
import {
  ComponentProps,
  createContext,
  ReactElement,
  useMemo,
  useState,
} from 'react';
import {NotificationEmptyState} from './notification-empty-state';
import {NotificationList} from './notification-list';

type Props = {
  className?: string;
  children?: ReactElement;
};

export const NotificationsDialogContext = createContext<{
  open: boolean;
  setOpen: (open: boolean) => void;
}>({
  open: false,
  setOpen: () => {},
});

export function NotificationsDialog({className, children}: Props) {
  const [open, setOpen] = useState(false);
  const contextValue = useMemo(() => ({open, setOpen}), [open, setOpen]);

  const trigger = children ?? (
    <Popover.Trigger
      aria-label="Notifications"
      className={cn('relative', className)}
      render={<Button variant="ghost" size="icon" />}
    >
      <BellIcon className="size-5" />
      <NotificationsTriggerBadge />
    </Popover.Trigger>
  );

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      {trigger}
      <Popover.Portal>
        <NotificationsDialogContext.Provider value={contextValue}>
          <NotificationsPopoverContent />
        </NotificationsDialogContext.Provider>
      </Popover.Portal>
    </Popover.Root>
  );
}

export function NotificationsTriggerBadge({className}: {className?: string}) {
  const {user} = useAuth();
  const count = notificationBadgeCount(user);
  if (!count) return null;
  return (
    <div
      className={cn(
        'ring-bg-background absolute inset-e-0 top-0 z-10 inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-warning px-1 text-xs font-semibold text-primary-foreground bg-blend-color ring-2 select-none max-md:hidden',
        className,
      )}
    >
      {count > 9 ? '9+' : count}
    </div>
  );
}

interface NotificationsDialogProps extends Omit<
  ComponentProps<typeof Popover.Content>,
  'children'
> {
  settingsLink?: string | null;
}

export function NotificationsPopoverContent({
  settingsLink,
  className,
  ...contentProps
}: NotificationsDialogProps) {
  const {user} = useAuth();
  const {notif} = useSettings();
  const query = useQuery(listNotificationsOptions({perPage: 50}));
  const markAsRead = useMutation(markNotificationsAsReadOptions());
  const deleteNotifications = useMutation(deleteNotificationsOptions());
  const hasUnread = !!user?.unread_notifications_count;
  const hasRead = !!query.data?.data.some(item => item.read_at);
  const [soundOn, setSoundOn] = useState(() => isNotificationSoundEnabled());

  const handleMarkAsRead = () => {
    if (!query.data?.data) return;
    markAsRead.mutate({
      markAllAsUnread: true,
    });
  };

  const handleClearRead = () => {
    deleteNotifications.mutate(['read']);
  };

  // "null" means that settings button should be hidden
  if (!settingsLink && settingsLink !== null) {
    settingsLink = '/account-settings/notifications';
  }

  return (
    <Popover.Content
      className={cn('w-md max-w-[calc(100vw-1rem)] gap-0 p-0', className)}
      {...contentProps}
    >
      <Popover.Header className="flex-row items-center border-b px-4 py-2">
        <Popover.Title>
          <Trans message="Notifications" />
        </Popover.Title>
        <div className="ml-auto flex items-center gap-1.5">
          <Button
            aria-label={soundOn ? 'Mute notification sound' : 'Unmute notification sound'}
            variant="ghost"
            size="icon"
            onClick={() => {
              const next = !soundOn;
              setNotificationSoundEnabled(next);
              setSoundOn(next);
              if (next) {
                unlockNotificationAudio();
              }
            }}
          >
            {soundOn ? <Volume2Icon /> : <VolumeXIcon />}
          </Button>
          {settingsLink && notif?.subs?.integrated && (
            <LinkButton
              aria-label="Notification settings"
              variant="ghost"
              size="icon"
              to={settingsLink}
            >
              <SettingsIcon />
            </LinkButton>
          )}
          {hasUnread && (
            <Button
              variant="outline"
              size="xs"
              onClick={handleMarkAsRead}
              disabled={markAsRead.isPending}
            >
              <CheckCheckIcon />
              <span className="max-md:hidden">
                <Trans message="Mark all as read" />
              </span>
            </Button>
          )}
          {hasRead && (
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Clear read notifications"
              onClick={handleClearRead}
              disabled={deleteNotifications.isPending}
            >
              <Trash2Icon />
            </Button>
          )}
        </div>
      </Popover.Header>
      <div className="compact-scrollbar max-h-170 min-h-41 overflow-y-auto overscroll-contain">
        <NotificationsList settingsLink={settingsLink} />
      </div>
      <div className="border-t px-3 py-2">
        <LinkButton
          variant="ghost"
          size="sm"
          className="w-full"
          to="/notifications"
        >
          <Trans message="Open notification center" />
        </LinkButton>
      </div>
    </Popover.Content>
  );
}

function NotificationsList({settingsLink}: {settingsLink?: string | null}) {
  const {data, isLoading} = useQuery(listNotificationsOptions({perPage: 50}));
  if (isLoading) {
    return (
      <div className="flex h-41 items-center justify-center">
        <Spinner aria-label="Loading notifications..." className="size-5" />
      </div>
    );
  }
  if (!data?.data.length) {
    return (
      <NotificationEmptyState
        settingsLink={settingsLink}
        className="px-0 py-6"
      />
    );
  }
  return (
    <div>
      <NotificationList notifications={data.data} />
    </div>
  );
}
